import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {ReportDataSource} from './data/ReportDataSource';
import {SavedReportRef} from './data/savedReportInput';
import {ReportRow} from './data/reportRowModel';
import {Condition, ReportDefinitionV1Tasks} from './contracts/report-definition.types';
import {fieldByKey, ReportTaxonomy} from './contracts/report-taxonomy.types';
import {defaultOperatorForDataType, operatorForValues} from './conditions/conditionOperators';
import {ReportTableState} from './ReportTable/ReportTable';
import {GroupingToken} from './ReportTable/useClientGrouping';
import {
	ACTIONS_COLUMN_ID,
	EXPANDER_COLUMN_ID,
	FAVORITE_COLUMN_ID,
	FIXED_COLUMN_IDS,
	flattenFieldPaths,
	orderedDataColumnIds,
} from './ReportTable/useReportTableColumns';
import {useViewerLayoutOverlay} from './useViewerLayoutOverlay';

export type BuilderMode = 'edit' | 'view';
export type BuilderRole = 'editor' | 'reader';

export interface ReportIntentPayload {
	reportType?: string;
	suggestedName?: string;
	definition: ReportDefinitionV1Tasks;
	summaries?: unknown[];
	flags?: unknown[];
	draftId?: string | null;
	// Original natural-language prompt the report was generated from. Rendered
	// (quoted, italic) in the AI banner. Two producers: the agent populates it on the
	// create_report_intent event for a fresh draft, and re-opening a saved report populates it from
	// the stored prompt. Optional either way; when absent the banner falls back to the static
	// "Generated from your prompt".
	prompt?: string;
	// Which surface authored the report. Absent on the fresh-intent path, where the builder is the
	// only possible author.
	source?: 'AI' | 'STANDARD';
}

export interface ReportBuilderContextValue {
	definition: ReportDefinitionV1Tasks; // working draft
	prompt?: string; // original NL prompt (AI banner); undefined when the payload omits it
	// Which surface authored the report, resolved. Drives the provenance banner: a report the builder
	// generated says so, a report opened from anywhere else does not.
	source: 'AI' | 'STANDARD';
	mode: BuilderMode;
	role: BuilderRole;
	dirty: boolean;
	tableState: ReportTableState;
	grouping: GroupingToken[];
	taxonomy: ReportTaxonomy | null;
	setMode(m: BuilderMode): void;
	setName(name: string): void;
	// The reporting period, as the date-range control shows it. Null members mean that end is open.
	period: {startDate: string | null; endDate: string | null};
	// Replaces the period. Rewrites the definition's date filter, so the table re-runs for the new
	// window; an open end drops that side of the filter rather than filtering on a missing date.
	setPeriod(startDate: string | null, endDate: string | null): void;
	setGrouping(g: GroupingToken[]): void;
	updateTableState(patch: Partial<ReportTableState>): void;
	// Serialises layout/sort/grouping into the definition and persists it into the Reports list via
	// `dataSource.saveDefinition` — creating the report on the first save and updating the same
	// report on every save after it. Resolves `true` when the report is saved and `false` when the
	// save failed; it never rejects, so a caller that forgets to handle failure gets a no-op rather
	// than an unhandled rejection that tears the surface down. A failed save leaves the draft dirty
	// and editable, which keeps Save enabled as the standing "not saved yet" signal.
	save(): Promise<boolean>;
	// Id of the Reports-list entry this draft has been saved as, or null while it is unsaved. Set by
	// the first successful `save()` and reused to update that entry rather than create duplicates.
	savedReportId: string | null;
	// True while a save is in flight. The surface must not allow a second save while set: a repeated
	// first save would create a second Reports-list entry for the same draft.
	saving: boolean;
	// Whether the report has a usable name. The name an unsaved draft arrives with is the agent's
	// suggestion, which is an acceptable name to save under; only a blank one blocks Save, because a
	// nameless entry in the Reports list cannot be identified or reopened.
	nameValid: boolean;
	toggleFavorite(rowId: string): void; // updates row state in place (mock)
	addCondition(fieldKey: string): void; // no-op when taxonomy is null or fieldKey is unknown
	updateConditionValues(index: number, values: string[]): void;
	removeCondition(index: number): void;
	rows: ReportRow[];
	totalCount: number;
	capped: boolean;
	cappedTotalEstimate: number;
	totalIsFloor: boolean;
	// False until the first fetch for the current definition resolves. Lets the surface tell
	// "not fetched yet" apart from "fetched, zero matches" so the empty state never claims the
	// report ran while the initial fetch is still in flight.
	hasFetched: boolean;
	// True when the fetch rejected (non-2xx or network failure). Kept separate from `hasFetched`
	// so the surface can show an error instead of the empty state — the empty state must only ever
	// claim "the report ran and matched nothing" after a real success.
	fetchFailed: boolean;
	// Re-runs the fetch for the current definition. The fetch is otherwise only triggered by a
	// definition change, so without this a transient network failure would be a dead end.
	retryFetch(): void;
}

const ReportBuilderContext = createContext<ReportBuilderContextValue | null>(null);

/** Collapse window for definition-driven refetches. Long enough that ticking several values in a
 * condition's picker issues one request instead of one per click, short enough that a single edit still
 * feels immediate next to the request's own latency. */
const FETCH_DEBOUNCE_MS = 400;

/** The period the date control should show. Prefers the definition's own dates and falls back to the
 * date filter, so a report saved before the period was editable still shows the window it filters on. */
function periodOf(definition: ReportDefinitionV1Tasks): {startDate: string | null; endDate: string | null} {
	const conditionValue = (field: string): string | null =>
		definition.filter.filterConditions.find(condition => condition.field === field)?.values?.[0] ?? null;
	return {
		startDate: definition.startDate ?? conditionValue('END_DATE'),
		endDate: definition.endDate ?? conditionValue('START_DATE'),
	};
}

/** Canonical `columnOrder` derived from the definition alone (no overlay/table-state input):
 * layout order when present, else field-tree order, wrapped by the fixed expander/favourite/actions
 * columns — reuses `useReportTableColumns`'s `orderedDataColumnIds` (Task 2) so the initial table
 * state matches what the columns hook will actually render (expander leading, favourite column
 * just before actions — see `useReportTableColumns`). */
function canonicalColumnOrder(definition: ReportDefinitionV1Tasks): string[] {
	return [EXPANDER_COLUMN_ID, ...orderedDataColumnIds(definition), FAVORITE_COLUMN_ID, ACTIONS_COLUMN_ID];
}

function canonicalColumnSizing(definition: ReportDefinitionV1Tasks): Record<string, number> {
	const sizing: Record<string, number> = {};
	for (const entry of definition.layout ?? []) {
		// A width that was never set is absent on an agent-generated definition but null on one read back
		// from a saved report (the stored column width is nullable), so both have to be treated as unset —
		// a null here would reach the table as a null column size.
		if (typeof entry.width === 'number') {
			sizing[entry.field] = entry.width;
		}
	}
	return sizing;
}

function canonicalSorting(definition: ReportDefinitionV1Tasks): ReportTableState['sorting'] {
	if (!definition.sort) {
		return [];
	}
	return [{id: definition.sort.field, desc: definition.sort.direction === 'DESC'}];
}

function canonicalTableState(definition: ReportDefinitionV1Tasks): ReportTableState {
	return {
		sorting: canonicalSorting(definition),
		columnOrder: canonicalColumnOrder(definition),
		columnSizing: canonicalColumnSizing(definition),
		columnVisibility: {},
	};
}

/** Serializes the current `tableState`/`grouping` into a NEW definition object (never mutates
 * `base`), per spec §6 save() rules: `layout` is `columnOrder` filtered of fixed (`__`-prefixed)
 * and hidden columns, paired with `columnSizing` when set; `sort` is the primary sort column, if
 * any; `grouping` is copied verbatim.
 *
 * Dropping hidden columns here does NOT persist their hidden-ness: layout carries order and width, and a
 * re-opened report rebuilds its column set from the field tree, so a hidden column comes back visible.
 * Hiding is session state until the definition contract gains a visibility concept. */
function serializeDefinition(
	base: ReportDefinitionV1Tasks,
	tableState: ReportTableState,
	grouping: GroupingToken[]
): ReportDefinitionV1Tasks {
	const layout = tableState.columnOrder
		.filter(id => !id.startsWith('__'))
		.filter(id => tableState.columnVisibility[id] !== false)
		.map(id => {
			const width = tableState.columnSizing[id];
			return width !== undefined ? {field: id, width} : {field: id};
		});

	const primarySort = tableState.sorting[0];
	const sort = primarySort
		? {field: primarySort.id, direction: (primarySort.desc ? 'DESC' : 'ASC') as 'ASC' | 'DESC'}
		: undefined;

	return {
		...base,
		layout,
		sort,
		grouping: grouping.slice() as ReportDefinitionV1Tasks['grouping'],
	};
}

export interface ReportBuilderProviderProps {
	payload: ReportIntentPayload;
	role?: BuilderRole;
	// Required: callers pass the transport explicitly (production: HttpReportDataSource via
	// ReportBuilderPage; tests/Storybook: a mock). No implicit default, so a mock can never be the
	// silent production fallback.
	dataSource: ReportDataSource;
	// Scopes the per-user localStorage layout overlay to the logged-in person. The page passes the
	// viewer's person id from the app's auth context; falls back to 'me' only when the id is unknown
	// (e.g. tests/Storybook that don't supply one).
	userId?: string;
	// Field metadata driving addCondition/updateConditionValues; null until the taxonomy for this
	// report type has loaded. addCondition no-ops while null.
	taxonomy?: ReportTaxonomy | null;
	children: React.ReactNode;
}

/** Recursively flips `favorite` on the row matching `rowId` (and leaves every other row, including
 * siblings/children, referentially unchanged) so React only re-renders the affected row/ancestors. */
function toggleFavoriteInRows(rows: ReportRow[], rowId: string): ReportRow[] {
	let changed = false;
	const next = rows.map(row => {
		if (row.id === rowId) {
			changed = true;
			return {...row, favorite: !row.favorite};
		}
		if (row.children.length > 0) {
			const nextChildren = toggleFavoriteInRows(row.children, rowId);
			if (nextChildren !== row.children) {
				changed = true;
				return {...row, children: nextChildren};
			}
		}
		return row;
	});
	return changed ? next : rows;
}

export function ReportBuilderProvider({
	payload,
	role = 'editor',
	dataSource,
	userId = 'me',
	taxonomy = null,
	children,
}: ReportBuilderProviderProps): JSX.Element {
	const resolvedDataSource = dataSource;
	const reportId = payload.draftId ?? 'draft';

	const [definition, setDefinition] = useState<ReportDefinitionV1Tasks>(payload.definition);
	const [mode, setMode] = useState<BuilderMode>(role === 'reader' ? 'view' : 'edit');
	const [dirty, setDirty] = useState(false);
	const [grouping, setGroupingState] = useState<GroupingToken[]>((payload.definition.grouping ?? []) as GroupingToken[]);
	const [rows, setRows] = useState<ReportRow[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [capped, setCapped] = useState(false);
	const [cappedTotalEstimate, setCappedTotalEstimate] = useState(0);
	const [totalIsFloor, setTotalIsFloor] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [fetchFailed, setFetchFailed] = useState(false);
	const [savedReport, setSavedReport] = useState<SavedReportRef | null>(null);
	const [saving, setSaving] = useState(false);
	// Mirrors `saving` for the reentrancy check in `save()`, which cannot wait for a render.
	const savingRef = useRef(false);
	// Bumped by `retryFetch` to re-run the fetch effect without changing the definition.
	const [fetchAttempt, setFetchAttempt] = useState(0);

	const validColumnIds = useMemo(() => {
		const ids = new Set(flattenFieldPaths(definition.fields));
		FIXED_COLUMN_IDS.forEach(id => ids.add(id));
		return ids;
	}, [definition.fields]);

	const {overlay, write: writeOverlay} = useViewerLayoutOverlay(reportId, userId, validColumnIds);

	const canonical = useMemo(() => canonicalTableState(definition), [definition]);

	// In-memory table state for the current session. Initialised from canonical + (for readers)
	// the persisted overlay on top; editors' in-memory edits flow through `save()` instead of the
	// overlay. Once a session has interacted with the table this local state is authoritative —
	// we intentionally do not re-derive it from `canonical`/`overlay` on every render (that would
	// stomp in-flight edits), only on definition identity change (see reset below).
	const initialTableState = useMemo<ReportTableState>(() => {
		if (role !== 'reader' || !overlay) {
			return canonical;
		}
		return {...canonical, ...overlay};
	}, [canonical]);

	const [tableState, setTableState] = useState<ReportTableState>(initialTableState);

	// Fetches on mount and whenever `definition` identity changes (e.g. after `save()`), plus on
	// demand via `retryFetch`. There is no loading spinner, so this stays fire-and-forget; a stale
	// in-flight response from a superseded definition is guarded against via the ref check.
	//
	// Edits are debounced, because a definition edit is not a discrete event: ticking values in a
	// condition's value picker produces a new definition per click, and running the whole report on each
	// one put dozens of fetches on the wire in a couple of seconds. The mount fetch is NOT debounced —
	// there is nothing to collapse it with, and delaying it would leave the surface blank for no reason.
	// `hasFetched` flips to true once the first response lands, so the empty state can wait for a
	// real (possibly zero-row) result rather than rendering during the initial in-flight window.
	// A rejection (non-2xx or network failure) is caught and surfaced as `fetchFailed` — leaving it
	// unhandled would both render a bare empty grid and raise an unhandled promise rejection.
	const latestDefinitionRef = useRef(definition);
	latestDefinitionRef.current = definition;
	// False until the mount fetch has been issued, which is one of the runs that must not be delayed.
	const hasScheduledFetchRef = useRef(false);
	// The `fetchAttempt` this effect last ran for, so an explicit retry can be told apart from an edit.
	const lastFetchAttemptRef = useRef(fetchAttempt);

	useEffect(() => {
		let cancelled = false;
		const isStale = (): boolean => cancelled || latestDefinitionRef.current !== definition;
		setFetchFailed(false);
		const runFetch = (): void => {
			if (cancelled) {
				return;
			}
			void resolvedDataSource.fetchRows(definition, {offset: 0, limit: 1000}).then(
				result => {
					if (isStale()) {
						return;
					}
					setRows(result.rows);
					setTotalCount(result.totalCount);
					setCapped(result.capped);
					setCappedTotalEstimate(result.cappedTotalEstimate);
					setTotalIsFloor(result.totalIsFloor);
					setHasFetched(true);
				},
				() => {
					if (isStale()) {
						return;
					}
					// Clear any previous result so a stale table can't linger under the error.
					setRows([]);
					setTotalCount(0);
					setCapped(false);
					setCappedTotalEstimate(0);
					setTotalIsFloor(false);
					setHasFetched(false);
					setFetchFailed(true);
				}
			);
		};

		// Only an edit-driven rerun is debounced. The mount fetch has nothing to collapse with, and a
		// retry is a deliberate click on "try again" — delaying either just makes the surface look stuck.
		const isRetry = fetchAttempt !== lastFetchAttemptRef.current;
		lastFetchAttemptRef.current = fetchAttempt;
		if (!hasScheduledFetchRef.current || isRetry) {
			hasScheduledFetchRef.current = true;
			runFetch();
			return () => {
				cancelled = true;
			};
		}

		const timer = setTimeout(runFetch, FETCH_DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [definition, resolvedDataSource, fetchAttempt]);

	const retryFetch = useCallback(() => {
		setFetchAttempt(n => n + 1);
	}, []);

	const setName = useCallback((name: string) => {
		setDefinition(prev => ({...prev, name}));
		setDirty(true);
	}, []);

	/** Tasks are matched by OVERLAP with the period, not containment: a task counts when it starts on or
	 * before the period ends and finishes on or after the period begins. Filtering for tasks fully inside
	 * the window would hide exactly the long-running work a period report is usually about. Each side is
	 * independent, so an open start or end simply drops its condition. */
	const setPeriod = useCallback((startDate: string | null, endDate: string | null) => {
		setDefinition(prev => {
			const filterConditions = prev.filter.filterConditions.filter(
				condition => condition.field !== 'START_DATE' && condition.field !== 'END_DATE'
			);
			if (endDate) {
				filterConditions.push({
					field: 'START_DATE',
					operator: 'LESS_OR_EQUAL',
					values: [endDate],
					context: 'TASKS',
				});
			}
			if (startDate) {
				filterConditions.push({
					field: 'END_DATE',
					operator: 'GREATER_OR_EQUAL',
					values: [startDate],
					context: 'TASKS',
				});
			}
			return {
				...prev,
				startDate: startDate ?? undefined,
				endDate: endDate ?? undefined,
				filter: {...prev.filter, filterConditions},
			};
		});
		setDirty(true);
	}, []);

	const setGrouping = useCallback((g: GroupingToken[]) => {
		setGroupingState(g);
		setDirty(true);
	}, []);

	const updateTableState = useCallback(
		(patch: Partial<ReportTableState>) => {
			setTableState(prev => ({...prev, ...patch}));
			if (role === 'reader') {
				writeOverlay(patch);
			} else {
				setDirty(true);
			}
		},
		[role, writeOverlay]
	);

	const save = useCallback(async () => {
		// Reentrancy guard, and deliberately a ref rather than the `saving` state: the state only gates
		// the Save button after a render has committed, so two calls raised before that commit would both
		// see `savedReport` as null, both take the create branch, and leave two Reports-list entries for
		// one draft with only the later one tracked. A ref is set synchronously, so the second call is
		// refused whatever the render timing.
		if (savingRef.current) {
			return false;
		}
		savingRef.current = true;
		const nextDefinition = serializeDefinition(definition, tableState, grouping);
		setSaving(true);
		try {
			const ref = await resolvedDataSource.saveDefinition(nextDefinition, savedReport, payload.prompt ?? null);
			setSavedReport(ref);
			// Adopting the serialised definition also re-runs the fetch (the effect keys on definition
			// identity), so what is on screen after a save is the saved definition run against live
			// data — the same thing re-opening the report from the Reports list will show.
			setDefinition(nextDefinition);
			setDirty(false);
			return true;
		} catch {
			// Deliberately swallowed. The draft is left dirty and editable so Save stays enabled and the
			// user can retry; re-throwing would surface as an unhandled rejection in any caller that
			// does not chain a catch, which is what tore the surface down before.
			return false;
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	}, [definition, tableState, grouping, resolvedDataSource, savedReport, payload.prompt]);

	const toggleFavorite = useCallback((rowId: string) => {
		setRows(prev => toggleFavoriteInRows(prev, rowId));
	}, []);

	const addCondition = useCallback(
		(fieldKey: string) => {
			if (!taxonomy) {
				return;
			}
			const field = fieldByKey(taxonomy, fieldKey);
			// Skip a field with no `filterField`: a condition stores the filterField enum, so a null
			// one would create a `field: null` condition that crashes label/summary rendering. Such
			// fields (non-filterable derived booleans) are never offered by the editor, but guard here
			// too so no caller can inject one.
			if (!field || !field.filterField) {
				return;
			}
			setDefinition(prev => ({
				...prev,
				filter: {
					...prev.filter,
					filterConditions: [
						...prev.filter.filterConditions,
						{
							// Conditions store the filterField enum (STATUS, PROJECT, ...), not the
							// taxonomy key the picker is keyed by; the editor passes the key, we store
							// the field's filterField so it round-trips through the report-service contract.
							field: field.filterField,
							operator: defaultOperatorForDataType() as Condition['operator'],
							values: [],
							context: field.context as Condition['context'],
						},
					],
				},
			}));
			setDirty(true);
		},
		[taxonomy]
	);

	const updateConditionValues = useCallback((index: number, values: string[]) => {
		setDefinition(prev => {
			const filterConditions = prev.filter.filterConditions.map((condition, i) =>
				i === index
					? {...condition, values, operator: operatorForValues(condition.operator, values) as Condition['operator']}
					: condition
			);
			return {...prev, filter: {...prev.filter, filterConditions}};
		});
		setDirty(true);
	}, []);

	const removeCondition = useCallback((index: number) => {
		setDefinition(prev => ({
			...prev,
			filter: {
				...prev.filter,
				filterConditions: prev.filter.filterConditions.filter((_, i) => i !== index),
			},
		}));
		setDirty(true);
	}, []);

	const value: ReportBuilderContextValue = {
		definition,
		prompt: payload.prompt,
		// The only way to reach the builder without a stored record is the chat intent, which the
		// builder itself produced, so an absent value defaults to 'AI'. The re-open path always
		// supplies an explicit value.
		source: payload.source ?? 'AI',
		mode,
		role,
		dirty,
		tableState,
		grouping,
		taxonomy,
		setMode,
		setName,
		period: periodOf(definition),
		setPeriod,
		setGrouping,
		updateTableState,
		save,
		savedReportId: savedReport?.id ?? null,
		saving,
		nameValid: definition.name.trim() !== '',
		toggleFavorite,
		addCondition,
		updateConditionValues,
		removeCondition,
		rows,
		totalCount,
		capped,
		cappedTotalEstimate,
		totalIsFloor,
		hasFetched,
		fetchFailed,
		retryFetch,
	};

	return <ReportBuilderContext.Provider value={value}>{children}</ReportBuilderContext.Provider>;
}

export function useReportBuilder(): ReportBuilderContextValue {
	const ctx = useContext(ReportBuilderContext);
	if (!ctx) {
		throw new Error('useReportBuilder must be used within a ReportBuilderProvider');
	}
	return ctx;
}
